import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';
import { 
  Database, 
  Key, 
  BarChart3, 
  Bell, 
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Gauge,
  LineChart,
  Table,
  LayoutGrid
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Data Source', icon: Database },
  { id: 2, title: 'Authentication', icon: Key },
  { id: 3, title: 'Define Metric', icon: BarChart3 },
  { id: 4, title: 'Visualization', icon: LayoutGrid },
  { id: 5, title: 'Alerts', icon: Bell },
  { id: 6, title: 'Review', icon: CheckCircle2 },
];

const DATA_SOURCES = [
  { value: 'graph', label: 'Microsoft Graph', description: 'M365, Entra ID, Teams' },
  { value: 'azure', label: 'Azure Cost Management', description: 'Spend, usage data' },
  { value: 'entra', label: 'Entra ID', description: 'Identity, security signals' },
  { value: 'health', label: 'Service Health', description: 'M365 service status' },
  { value: 'api', label: 'Custom API', description: 'Any REST endpoint' },
  { value: 'webhook', label: 'Webhook', description: 'Push-based data' },
  { value: 'csv', label: 'CSV/Manual', description: 'Upload or manual entry' },
];

const AUTH_METHODS = [
  { value: 'oauth', label: 'OAuth 2.0', description: 'Recommended for Microsoft services' },
  { value: 'apikey', label: 'API Key', description: 'Simple token authentication' },
  { value: 'basic', label: 'Basic Auth', description: 'Username/password' },
  { value: 'none', label: 'None', description: 'Public endpoints' },
];

const VISUALIZATIONS = [
  { value: 'card', label: 'Metric Card', icon: LayoutGrid },
  { value: 'line', label: 'Line Chart', icon: LineChart },
  { value: 'bar', label: 'Bar Chart', icon: BarChart3 },
  { value: 'gauge', label: 'Gauge', icon: Gauge },
  { value: 'table', label: 'Data Table', icon: Table },
];

const CATEGORIES = [
  'Executive Overview',
  'Microsoft 365',
  'Azure',
  'Security',
  'Operations',
  'Governance',
  'Custom',
];

export const MetricWizard = ({ open, onOpenChange, onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    dataSource: '',
    authMethod: 'oauth',
    endpoint: '',
    apiKey: '',
    unit: '',
    refreshInterval: 15,
    visualization: 'card',
    category: 'Custom',
    alertEnabled: false,
    alertThreshold: '',
    alertDirection: 'above',
    notifyEmail: false,
    notifyTeams: false,
    notifyWebhook: false,
    webhookUrl: '',
  });

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleTest = async () => {
    setIsLoading(true);
    // Simulate API test
    await new Promise(resolve => setTimeout(resolve, 1500));
    setTestResult({ success: true, message: 'Connection successful! Sample value: 42' });
    setIsLoading(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    const channels = [];
    if (formData.notifyEmail) channels.push('email');
    if (formData.notifyTeams) channels.push('teams');
    if (formData.notifyWebhook) channels.push('webhook');

    const metric = {
      name: formData.name,
      data_source: formData.dataSource,
      endpoint: formData.endpoint,
      auth_method: formData.authMethod,
      unit: formData.unit,
      refresh_interval: formData.refreshInterval,
      visualization: formData.visualization,
      category: formData.category,
      alert_threshold: formData.alertEnabled ? parseFloat(formData.alertThreshold) : null,
      alert_direction: formData.alertEnabled ? formData.alertDirection : null,
      notification_channels: channels,
    };

    await onSave?.(metric);
    setIsLoading(false);
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      name: '',
      dataSource: '',
      authMethod: 'oauth',
      endpoint: '',
      apiKey: '',
      unit: '',
      refreshInterval: 15,
      visualization: 'card',
      category: 'Custom',
      alertEnabled: false,
      alertThreshold: '',
      alertDirection: 'above',
      notifyEmail: false,
      notifyTeams: false,
      notifyWebhook: false,
      webhookUrl: '',
    });
    setTestResult(null);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select the source of your metric data
            </p>
            <div className="grid grid-cols-1 gap-3">
              {DATA_SOURCES.map((source) => (
                <div
                  key={source.value}
                  data-testid={`source-${source.value}`}
                  className={cn(
                    'p-4 rounded-xl border cursor-pointer transition-colors',
                    formData.dataSource === source.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                  onClick={() => updateForm('dataSource', source.value)}
                >
                  <div className="font-medium">{source.label}</div>
                  <div className="text-sm text-muted-foreground">{source.description}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Configure authentication for your data source
            </p>
            <div className="grid grid-cols-2 gap-3">
              {AUTH_METHODS.map((method) => (
                <div
                  key={method.value}
                  data-testid={`auth-${method.value}`}
                  className={cn(
                    'p-4 rounded-xl border cursor-pointer transition-colors',
                    formData.authMethod === method.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                  onClick={() => updateForm('authMethod', method.value)}
                >
                  <div className="font-medium text-sm">{method.label}</div>
                  <div className="text-xs text-muted-foreground">{method.description}</div>
                </div>
              ))}
            </div>
            {formData.authMethod === 'apikey' && (
              <div className="pt-4">
                <Label htmlFor="apiKey">API Key</Label>
                <Input
                  id="apiKey"
                  type="password"
                  placeholder="Enter your API key"
                  value={formData.apiKey}
                  onChange={(e) => updateForm('apiKey', e.target.value)}
                  className="mt-2"
                  data-testid="api-key-input"
                />
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Metric Name</Label>
              <Input
                id="name"
                placeholder="e.g., Active Users Count"
                value={formData.name}
                onChange={(e) => updateForm('name', e.target.value)}
                className="mt-2"
                data-testid="metric-name-input"
              />
            </div>
            <div>
              <Label htmlFor="endpoint">API Endpoint / Query</Label>
              <Input
                id="endpoint"
                placeholder="e.g., /users?$count=true"
                value={formData.endpoint}
                onChange={(e) => updateForm('endpoint', e.target.value)}
                className="mt-2"
                data-testid="endpoint-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  placeholder="e.g., users, $, %"
                  value={formData.unit}
                  onChange={(e) => updateForm('unit', e.target.value)}
                  className="mt-2"
                  data-testid="unit-input"
                />
              </div>
              <div>
                <Label htmlFor="interval">Refresh Interval (min)</Label>
                <Input
                  id="interval"
                  type="number"
                  min={1}
                  value={formData.refreshInterval}
                  onChange={(e) => updateForm('refreshInterval', parseInt(e.target.value))}
                  className="mt-2"
                  data-testid="interval-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => updateForm('category', value)}
              >
                <SelectTrigger className="mt-2" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Choose how to display your metric on the dashboard
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {VISUALIZATIONS.map((viz) => {
                const Icon = viz.icon;
                return (
                  <div
                    key={viz.value}
                    data-testid={`viz-${viz.value}`}
                    className={cn(
                      'p-4 rounded-xl border cursor-pointer transition-colors text-center',
                      formData.visualization === viz.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    )}
                    onClick={() => updateForm('visualization', viz.value)}
                  >
                    <Icon className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <div className="font-medium text-sm">{viz.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="alertEnabled"
                checked={formData.alertEnabled}
                onCheckedChange={(checked) => updateForm('alertEnabled', checked)}
                data-testid="alert-enabled-checkbox"
              />
              <Label htmlFor="alertEnabled">Enable alerts for this metric</Label>
            </div>

            {formData.alertEnabled && (
              <>
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div>
                    <Label htmlFor="threshold">Threshold Value</Label>
                    <Input
                      id="threshold"
                      type="number"
                      placeholder="100"
                      value={formData.alertThreshold}
                      onChange={(e) => updateForm('alertThreshold', e.target.value)}
                      className="mt-2"
                      data-testid="threshold-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="direction">Alert When</Label>
                    <Select
                      value={formData.alertDirection}
                      onValueChange={(value) => updateForm('alertDirection', value)}
                    >
                      <SelectTrigger className="mt-2" data-testid="direction-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="above">Above threshold</SelectItem>
                        <SelectItem value="below">Below threshold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-4">
                  <Label className="mb-3 block">Notification Channels</Label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="notifyEmail"
                        checked={formData.notifyEmail}
                        onCheckedChange={(checked) => updateForm('notifyEmail', checked)}
                        data-testid="notify-email-checkbox"
                      />
                      <Label htmlFor="notifyEmail">Email</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="notifyTeams"
                        checked={formData.notifyTeams}
                        onCheckedChange={(checked) => updateForm('notifyTeams', checked)}
                        data-testid="notify-teams-checkbox"
                      />
                      <Label htmlFor="notifyTeams">Microsoft Teams</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="notifyWebhook"
                        checked={formData.notifyWebhook}
                        onCheckedChange={(checked) => updateForm('notifyWebhook', checked)}
                        data-testid="notify-webhook-checkbox"
                      />
                      <Label htmlFor="notifyWebhook">Webhook</Label>
                    </div>
                    {formData.notifyWebhook && (
                      <Input
                        placeholder="https://your-webhook-url.com"
                        value={formData.webhookUrl}
                        onChange={(e) => updateForm('webhookUrl', e.target.value)}
                        className="mt-2"
                        data-testid="webhook-url-input"
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-muted/50 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Metric Name</span>
                <span className="text-sm font-medium">{formData.name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Data Source</span>
                <span className="text-sm font-medium">
                  {DATA_SOURCES.find(s => s.value === formData.dataSource)?.label || '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Authentication</span>
                <span className="text-sm font-medium">
                  {AUTH_METHODS.find(m => m.value === formData.authMethod)?.label || '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Endpoint</span>
                <span className="text-sm font-medium font-mono">{formData.endpoint || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Visualization</span>
                <span className="text-sm font-medium">
                  {VISUALIZATIONS.find(v => v.value === formData.visualization)?.label || '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Refresh</span>
                <span className="text-sm font-medium">{formData.refreshInterval} min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Alerts</span>
                <span className="text-sm font-medium">
                  {formData.alertEnabled ? `${formData.alertDirection} ${formData.alertThreshold}` : 'Disabled'}
                </span>
              </div>
            </div>

            <Button 
              onClick={handleTest} 
              variant="outline" 
              className="w-full"
              disabled={isLoading}
              data-testid="test-connection-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                'Test Connection'
              )}
            </Button>

            {testResult && (
              <div className={cn(
                'p-3 rounded-lg text-sm',
                testResult.success ? 'bg-[rgb(var(--success))]/10 text-[rgb(var(--success))]' : 'bg-destructive/10 text-destructive'
              )}>
                {testResult.message}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="metric-wizard-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Custom Metric</DialogTitle>
          <DialogDescription>
            Follow the steps to add a new metric to your dashboard
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between px-4 py-6">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                      isActive && 'bg-primary text-primary-foreground',
                      isCompleted && 'bg-[rgb(var(--success))] text-white',
                      !isActive && !isCompleted && 'bg-muted text-muted-foreground'
                    )}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={cn(
                    'text-xs mt-2 hidden md:block',
                    isActive && 'text-primary font-medium',
                    !isActive && 'text-muted-foreground'
                  )}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={cn(
                    'w-12 h-0.5 mx-2',
                    isCompleted ? 'bg-[rgb(var(--success))]' : 'bg-muted'
                  )} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px] px-2">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {currentStep < STEPS.length ? (
            <Button
              onClick={handleNext}
              disabled={currentStep === 1 && !formData.dataSource}
              data-testid="wizard-next-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={isLoading || !formData.name}
              data-testid="wizard-save-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Metric'
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
